import {
  PatientBillingSummaryResponseDTO,
  PatientDTO,
  PatientPaymentRefundRequestDTO,
} from 'dtos';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';
import {
  AdjustmentFormData,
  RefundPaymentMethod,
} from 'views/EMR/Billing/AdjustPayment/AdjustmentForm';

export function getTotalAdjustedAmount(
  encounters: EncounterWithPayment[],
  isMoveToCredit: boolean,
  isSquareRefund: boolean,
) {
  let total = 0;
  const key = (isMoveToCredit
    ? 'moveToCreditAmount'
    : 'refundAmount') as keyof EncounterWithPayment;

  const currentEncounters = (isMoveToCredit || isSquareRefund)
    ? encounters.filter((encounter) => !encounter.isCredit)
    : encounters;

  const isInvalid = isNil(currentEncounters)
        || currentEncounters.some((encounter) =>
          Number.isNaN(Number(encounter[key]))
          || Number(encounter[key]) <= 0
          || Number(encounter[key]) > (encounter.totalPatientPaid ?? 0));
  if (!isInvalid) {
    total = sumBy(currentEncounters, (encounter) => Number(encounter[key]) ?? 0);
  }

  return total;
}

const amountKeys = ['refundAmount', 'moveToCreditAmount'] as const;
const allowedAmountKeys = ['totalPatientPaid', 'netSquarePaidAmount'] as const;

export function getAdjustmentError(
  encounters: EncounterWithPayment[],
  patient: PatientDTO | null,
  isMoveToCredit: boolean,
  isSquareRefund: boolean,
  paymentMethod: RefundPaymentMethod | null = null,
) {
  let error = null;

  const currentEncounters = (isMoveToCredit || isSquareRefund)
    ? encounters.filter((encounter) => !encounter.isCredit)
    : encounters;

  const amountKey = amountKeys[Number(isMoveToCredit)];
  const allowedAmountKey = allowedAmountKeys[Number(isSquareRefund)];

  const refundMethod = paymentMethod?.refundMethod;
  const isCardOrCheckRefundError = /^(manualCardRefund|check)/i.test(refundMethod?.discriminator ?? '') && isEmptyString(paymentMethod?.transactionIdentifier);

  if (isNil(patient)) {
    error = 'Select patient';
  } else if (!isMoveToCredit && isNil(refundMethod)) {
    error = 'Select refund method';
  } else if (isCardOrCheckRefundError) {
    error = 'Enter check number or transaction ID';
  } else if (isEmpty(currentEncounters)) {
    error = `Enter an amount to ${isMoveToCredit ? 'move' : 'refund'}`;
  } else if (currentEncounters.some(
    (encounter) => Number(encounter[amountKey] ?? 0) > Number(encounter[allowedAmountKey] ?? 0),
  )) {
    error = `Amount cannot exceed patient paid${isSquareRefund ? ' by Square' : ''}`;
  } else if (currentEncounters.some((encounter) => Number(encounter[amountKey] ?? 0) <= 0)) {
    error = 'Amount must be greater than zero';
  }
  return error;
}

export function getEncountersWithPaymentInfo(
  data: EncounterWithPayment[],
  encounters: EncounterWithPayment[],
  billingSummary: PatientBillingSummaryResponseDTO,
  isMoveToCredit: boolean,
  isSquareRefund: boolean,
) {
  const encountersWithPaymentInfo = data?.map((encounter) => ({
    ...encounter,
    ...(encounters?.find((item) => item.encounterId === encounter.encounterId) ?? {}),
  })) ?? [];

  const creditRow = {
    isCredit: true,
    totalPatientPaid: billingSummary?.credit ?? 0,
    patientBalance: billingSummary?.totalBalance ?? 0,
    refundAmount: encounters?.find((item) => item.isCredit)?.refundAmount,
    suggestedAmount: billingSummary?.credit ?? 0,
  };

  const allowedAmountKey = ((isMoveToCredit || !isSquareRefund)
    ? 'totalPatientPaid'
    : 'netSquarePaidAmount') as keyof EncounterWithPayment;

  return (
    (isMoveToCredit || isSquareRefund)
      ? encountersWithPaymentInfo
      : [...encountersWithPaymentInfo, creditRow]
  ).filter((encounter: EncounterWithPayment) => Number(encounter[allowedAmountKey] ?? 0) > 0);
}

function serializeMoveToCreditEncounters(encounters: EncounterWithPayment[]) {
  return encounters.map((encounter) => ({
    encounterId: encounter.encounterId,
    amount: encounter?.moveToCreditAmount ?? 0,
  }));
}

function serializeRefundEncounters(encounters: EncounterWithPayment[]) {
  return encounters.map((encounter) => ({
    encounterId: encounter.encounterId,
    amount: encounter?.refundAmount ?? 0,
  })).filter((encounter) => !isNil(encounter.encounterId));
}

export function serializeMoveToCreditPayload(data: AdjustmentFormData) {
  return {
    encounterWithAmounts: serializeMoveToCreditEncounters(data?.encounters ?? []),
  };
}

export function serializeRefundPayload({
  encounters = [],
  paymentMethod,
  patient = {},
  comment,
}: AdjustmentFormData): PatientPaymentRefundRequestDTO {
  const isSquareRefund = /SquareRefund/i.test(paymentMethod?.refundMethod?.discriminator ?? '');
  const isCheckOrCardRefund = /^(manualCardRefund|check)/i.test(paymentMethod?.refundMethod?.discriminator ?? '');

  // We cannot refund credit with Square, so for square, it is 0
  const creditRefund = isSquareRefund
    ? 0
    : encounters?.find(
      (encounter) => encounter.isCredit,
    )?.refundAmount ?? 0;

  const total = getTotalAdjustedAmount(encounters ?? [], false, isSquareRefund);
  return {
    encounterRefunds: serializeRefundEncounters(encounters ?? []),
    creditRefund,
    patientId: patient?.patientId ?? 0,
    refundMethod: {
      refundAmount: total,
      discriminator: paymentMethod?.refundMethod?.discriminator ?? '',
      transactionIdentifier: isCheckOrCardRefund
        ? paymentMethod?.transactionIdentifier?.slice(0, 100) ?? null
        : null,
    },
    comment: isEmptyString(comment) ? null : comment,
  };
}
